// ═══════════════════════════════════════════════════════════════
// MainStreet — Damage Reports (Submit + Admin List)
// Merged DamageReport + AdminDamageReports
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Textarea,
  Select, MultiSelect, Modal, Pagination,
} from '../../components/gab/GabComponents';

const DR_PAGE_SIZE = 25;
import { useDisclosure } from '../../components/gab/GabHooks';
import { notifications } from '../../lib/notifications';
import {
  IconPlus, IconRefresh, IconAlertTriangle,
} from '@tabler/icons-react';
import { useMainStreetOpsStore } from '../../store/mainstreetOpsStore';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { OP_STATUS, DAMAGE_TYPES } from './constants';
import { fmtDate } from '../../lib/dateFormat';

// ── Helpers ──────────────────────────────────────────────────

const OP_SELECT = OP_STATUS.map(s => ({ value: s.id, label: s.label }));
const DAMAGE_SELECT = DAMAGE_TYPES.map(d => ({ value: d.id, label: d.label }));

const SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'major', label: 'Major' },
  { value: 'severe', label: 'Severe' },
  { value: 'destroyed', label: 'Destroyed' },
];

const SEVERITY_COLORS: Record<string, string> = {
  minor: 'var(--color-success)',
  moderate: '#C68700',
  major: '#F86A0B',
  severe: 'var(--color-error)',
  destroyed: 'dark',
};

function opLabel(id: string): string {
  return OP_STATUS.find(s => s.id === id)?.label ?? id;
}

function damageLabel(id: string): string {
  return DAMAGE_TYPES.find(d => d.id === id)?.label ?? id;
}

// ═════════════════════════════════════════════════════════════
export default function DamageReports() {
  const { canEdit } = useModulePermission('mainstreet');
  const {
    damageReports, loading,
    loadDamageReports, createDamageReport,
  } = useMainStreetOpsStore();
  const { organizations, loadOrganizations } = useMainStreetStore();
  const { activeIncident, user } = useAuthStore();

  // Create modal
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [orgId, setOrgId] = useState('');
  const [opStatus, setOpStatus] = useState('');
  const [damageTypes, setDamageTypes] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('');
  const [saving, setSaving] = useState(false);

  // Filters
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [drPage, setDrPage] = useState(1);

  useEffect(() => {
    loadDamageReports();
    loadOrganizations();
  }, []);

  const orgOptions = useMemo(() =>
    organizations.map(o => ({ value: String(o.id || ''), label: String(o.name || `Org #${o.id}`) })),
    [organizations],
  );

  const orgFilterOptions = useMemo(() => [
    { value: 'all', label: 'All Organizations' },
    ...orgOptions,
  ], [orgOptions]);

  const sevFilterOptions = [
    { value: 'all', label: 'All Severities' },
    ...SEVERITY_OPTIONS,
  ];

  const filtered = useMemo(() => {
    let list = damageReports; // V2 has no incidentId field on this table — show all
    if (filterOrg !== 'all') list = list.filter(r => String(r.orgId) === filterOrg);
    if (filterSeverity !== 'all') list = list.filter(r => r.severity === filterSeverity);
    return [...list].sort((a, b) => (b.reportedAt ?? '').localeCompare(a.reportedAt ?? ''));
  }, [damageReports, filterOrg, filterSeverity]);

  useEffect(() => { setDrPage(1); }, [filterOrg, filterSeverity]);
  const drTotalPages = Math.max(1, Math.ceil(filtered.length / DR_PAGE_SIZE));
  const pagedDr = filtered.slice((drPage - 1) * DR_PAGE_SIZE, drPage * DR_PAGE_SIZE);

  function orgName(id: string): string {
    return organizations.find(o => String(o.id) === id)?.name ?? id;
  }

  // ── Create ─────────────────────────────────────────────────

  async function handleCreate() {
    if (!orgId || !severity || !opStatus) {
      notifications.show({ color: 'var(--color-error)', title: 'Required', message: 'Organization, operational status, and severity are required.' });
      return;
    }
    const org = organizations.find(o => String(o.id) === orgId);
    setSaving(true);
    await createDamageReport({
      orgId,
      orgName: org?.name ?? '',
      incidentId: activeIncident ? String(activeIncident.id) : '',
      opStatus,
      damageTypes: damageTypes.join(','),
      description: description.trim(),
      severity,
      reportedBy: user?.fullName ?? user?.email ?? 'Unknown',
      reportedAt: new Date().toISOString(),
    });
    setSaving(false);
    notifications.show({ color: 'var(--color-success)', title: 'Created', message: 'Damage report submitted.' });
    setOrgId(''); setOpStatus(''); setDamageTypes([]); setDescription(''); setSeverity('');
    closeCreate();
  }

  // ── Render ─────────────────────────────────────────────────

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Damage Reports</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loading} onClick={() => loadDamageReports()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Submit Report
          </Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <Select data={orgFilterOptions} value={filterOrg}
          onChange={v => setFilterOrg(v ?? 'all')} size="xs" w={220} searchable />
        <Select data={sevFilterOptions} value={filterSeverity}
          onChange={v => setFilterSeverity(v ?? 'all')} size="xs" w={180} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} report{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Organization</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Op Status</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Damage Types</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Severity</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Reporter</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loading ? 'Loading...' : 'No damage reports found'}
                  </td>
                </tr>
              )}
              {pagedDr.map(report => (
                <tr key={report.id}
                  style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '8px 16px', fontWeight: 500 }}>
                    {report.orgName || orgName(report.orgId)}
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    {report.opStatus ? (
                      <Badge size="xs" color={OP_STATUS.find(s => s.id === report.opStatus)?.color ?? '#939598'}>
                        {opLabel(report.opStatus)}
                      </Badge>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <Group gap={4}>
                      {report.damageTypes
                        ? (report.damageTypes || '').split(',').map(d => (
                            <Badge key={d} size="xs" variant="outline">{damageLabel(d.trim())}</Badge>
                          ))
                        : '-'}
                    </Group>
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <Badge size="xs" color={SEVERITY_COLORS[report.severity] ?? '#939598'}>
                      {report.severity || '-'}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 16px' }}>{report.reportedBy || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>{fmtDate(report.reportedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {drTotalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={drTotalPages} value={drPage} onChange={setDrPage} size="sm" />
        </Group>
      )}

      {/* ── Create Modal ────────────────────────────────────── */}
      <Modal opened={createOpened} onClose={closeCreate} title="Submit Damage Report" size="lg">
        <Select label="Organization" data={orgOptions} value={orgId}
          onChange={v => setOrgId(v ?? '')} placeholder="Select organization"
          size="sm" mb="sm" searchable required />

        <Select label="Operational Status" data={OP_SELECT} value={opStatus}
          onChange={v => setOpStatus(v ?? '')} placeholder="Current status"
          size="sm" mb="sm" required />

        <MultiSelect label="Damage Types" data={DAMAGE_SELECT} value={damageTypes}
          onChange={setDamageTypes} placeholder="Select damage types"
          size="sm" mb="sm" searchable />

        <Select label="Severity" data={SEVERITY_OPTIONS} value={severity}
          onChange={v => setSeverity(v ?? '')} placeholder="Select severity"
          size="sm" mb="sm" required />

        <Textarea label="Description" value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Describe the damage..." size="sm" mb="md" autosize minRows={3} />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={closeCreate}>Cancel</Button>
          <Button loading={saving} onClick={handleCreate}
            leftSection={<IconAlertTriangle size={16} />}>Submit Report</Button>
        </Group>
      </Modal>
    </div>
  );
}
